// AdminRateCards — admin-only management for PM-POSHAN rate cards
//   • Period-wise (effectiveFrom / effectiveTo + period label)
//   • Class-wise (Bal Vatika / Primary / Upper Primary / High School)
//   • Per-student cooking cost (Rs / day)
//   • Ingredient grid (name · qty in g · rate per kg)
//
// Mounted under Admin Dashboard → tab "Rate Cards".
// Backend: /api/rate-cards + /api/admin/rate-cards
//
// UX notes
// ────────
//   • Top toolbar to create a new card from a class+period combination.
//   • Cards list grouped by period, then class. Edit-in-place dialog.
//   • Quick "Duplicate to next FY" button — clones an existing card with
//     the period label bumped by 1 year so admins don't re-type 30 rows.

import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { refreshRateCards } from "@/lib/useRateCardsSync";
import {
  Plus, Pencil, Trash2, Copy, Save, X, CalendarRange, IndianRupee, RefreshCw, Sparkles,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLASS_OPTIONS = [
  { v: "balVatika",    label: "Bal Vatika",    od: "ବାଳ ବାଟିକା" },
  { v: "primary",      label: "Primary",       od: "ପ୍ରାଥମିକ" },
  { v: "upperPrimary", label: "Upper Primary", od: "ଉଚ୍ଚ ପ୍ରାଥମିକ" },
  { v: "highSchool",   label: "High School",   od: "ଉଚ୍ଚ ବିଦ୍ୟାଳୟ" },
];
const CLASS_LABEL = Object.fromEntries(CLASS_OPTIONS.map((c) => [c.v, c.label]));
// Sort key used to render class rows in PM-POSHAN order: Bal Vatika → Primary →
// Upper Primary → High School. Map index → rank so unknown classes fall to end.
const CLASS_ORDER = Object.fromEntries(CLASS_OPTIONS.map((c, i) => [c.v, i]));

// Rate "per unit" semantics:
//   • Rice/Dal/Soya/Masala/Fuel/Veg/Ragi/Wheat/Sugar/Sesame/GroundNuts/Cardamom
//     are priced per kg, but daily qty is stored in grams → rateLabel "/ kg".
//   • Oil / Refined Oil → "/ L"   (qty stored in ml)
//   • Egg / Ladoo Making Cost → "/ pc"   (qty is a count, rate is the literal piece price)
function rateUnitFor(ing) {
  const u = (ing.unit || "g").toLowerCase();
  if (u === "pc" || u === "piece" || u === "pcs") return "pc";
  if (u === "ml" || u === "l")  return "L";
  return "kg";
}
// Cost contribution per student / day for one ingredient row.
function rowCost(ing) {
  const q = parseFloat(ing.qtyG) || 0;
  const r = parseFloat(ing.ratePerKg) || 0;
  const u = rateUnitFor(ing);
  // kg / L → qty is in g / ml, so divide by 1000
  if (u === "kg" || u === "L") return (q * r) / 1000;
  // per piece — qty * rate directly
  return q * r;
}

// Seed ingredient list mirrors what the daily-entry / grocery flow uses.
const DEFAULT_INGREDIENTS = [
  { name: "Rice",              qtyG: 100, unit: "g",  ratePerKg: 0 },
  { name: "Dal",               qtyG: 20,  unit: "g",  ratePerKg: 0 },
  { name: "Vegetables",        qtyG: 50,  unit: "g",  ratePerKg: 0 },
  { name: "Oil",               qtyG: 5,   unit: "ml", ratePerKg: 0 },
  { name: "Salt & Spices",     qtyG: 5,   unit: "g",  ratePerKg: 0 },
  { name: "Fuel",              qtyG: 0,   unit: "g",  ratePerKg: 0 },
  { name: "Egg",               qtyG: 1,   unit: "pc", ratePerKg: 0 },
  { name: "Ladoo Making Cost", qtyG: 1,   unit: "pc", ratePerKg: 0 },
];

function emptyDraft() {
  const today = new Date().toISOString().slice(0, 10);
  return {
    id: null,
    period: "",
    effectiveFrom: today,
    effectiveTo: "",
    className: "primary",
    cookingCostPerStu: 0,
    ingredients: DEFAULT_INGREDIENTS.map((x) => ({ ...x })),
    notes: "",
  };
}

export default function AdminRateCards() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/rate-cards`);
      setCards(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load rate cards");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  // Group cards by period for the listing.
  const grouped = useMemo(() => {
    const m = new Map();
    [...cards]
      .sort((a, b) => (a.effectiveFrom < b.effectiveFrom ? 1 : -1))
      .forEach((c) => {
        const key = c.period || c.effectiveFrom;
        if (!m.has(key)) m.set(key, []);
        m.get(key).push(c);
      });
    return Array.from(m.entries());
  }, [cards]);

  const onSave = async () => {
    if (!draft) return;
    if (!draft.period.trim()) { toast.error("Period label is required (e.g. FY 2026-27 or Q4 FY 2025-26)"); return; }
    if (!draft.effectiveFrom) { toast.error("Effective From date required"); return; }
    try {
      await axios.post(`${API}/admin/rate-cards`, draft);
      toast.success(draft.id ? "Rate card updated" : "Rate card created");
      setDraft(null);
      await refresh();
      // iter144 — push new qty/rate to Daily Entry + Reports calculations
      // immediately (no page reload needed).
      refreshRateCards();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save rate card");
    }
  };

  const onDelete = async (card) => {
    if (!confirm(`Delete rate card "${card.period}" · ${CLASS_LABEL[card.className]}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/rate-cards/${card.id}`);
      toast.success("Rate card deleted");
      refresh();
      refreshRateCards();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  // Seed all 4 classes for a period in one click — pulls the canonical
  // PM-POSHAN qty + rate sheet from the backend (`mealPlan.js` parity) so
  // admin doesn't hand-type 60 rows on first setup.
  const [seedOpen, setSeedOpen] = useState(false);
  const [seed, setSeed] = useState({
    period: "FY 2025-26",
    effectiveFrom: "2025-04-01",
    effectiveTo: "2026-03-31",
    overwrite: false,
  });
  const onSeedDefaults = async () => {
    if (!seed.period.trim() || !seed.effectiveFrom) {
      toast.error("Period label and Effective From are required");
      return;
    }
    try {
      const r = await axios.post(`${API}/admin/rate-cards/seed-defaults`, seed);
      const created = r.data?.seeded?.length || 0;
      const skipped = r.data?.skipped?.length || 0;
      toast.success(`Seeded ${created} class row(s)${skipped ? `, skipped ${skipped} existing` : ""}`);
      setSeedOpen(false);
      await refresh();
      refreshRateCards();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seed failed");
    }
  };

  const onDuplicate = (card) => {
    const next = { ...card, id: null };
    // Bump the period label by 1 year heuristically (e.g. "FY 2025-26" → "FY 2026-27").
    const m = (card.period || "").match(/(\d{4})\D+(\d{2,4})/);
    if (m) {
      const a = parseInt(m[1], 10) + 1;
      const b = (parseInt(m[2], 10) + 1) % 100;
      next.period = card.period.replace(m[0], `${a}-${String(b).padStart(2, "0")}`);
    }
    // Shift dates by 1 year.
    const shift = (iso) => {
      if (!iso) return iso;
      const [y, mo, d] = iso.split("-");
      return `${parseInt(y, 10) + 1}-${mo}-${d}`;
    };
    next.effectiveFrom = shift(card.effectiveFrom);
    if (card.effectiveTo) next.effectiveTo = shift(card.effectiveTo);
    setDraft(next);
  };

  return (
    <section className="space-y-4" data-testid="admin-rate-cards-tab">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-heading text-xl font-bold text-ink-900">PM-POSHAN Rate Cards</h2>
        <span className="text-xs text-ink-700">
          Manage classwise ingredient qty + rate and per-student cooking cost · period-wise.
        </span>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={refresh} data-testid="rate-cards-refresh">
            <RefreshCw className="mr-1 h-4 w-4" /> Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSeedOpen(true)}
            data-testid="rate-cards-seed"
            className="border-moss/40 text-moss-dark hover:bg-moss/10"
          >
            <Sparkles className="mr-1 h-4 w-4" /> Seed PM-POSHAN Defaults
          </Button>
          <Button
            size="sm"
            onClick={() => setDraft(emptyDraft())}
            data-testid="rate-cards-new"
            className="bg-clay text-white hover:bg-clay-dark"
          >
            <Plus className="mr-1 h-4 w-4" /> New Rate Card
          </Button>
        </div>
      </div>

      {loading && <p className="text-sm text-ink-700">Loading…</p>}
      {!loading && grouped.length === 0 && (
        <Card data-testid="rate-cards-empty">
          <CardContent className="py-12 text-center">
            <p className="font-heading text-lg text-ink-900">No rate cards yet</p>
            <p className="mt-1 text-sm text-ink-700">
              Click <b>New Rate Card</b> to add the first one. You will set a period (e.g. <i>FY 2025-26</i>),
              pick a class, then enter ingredient quantities, procurement rates and per-student cooking cost.
            </p>
          </CardContent>
        </Card>
      )}

      {/* iter140 — Year-wise × Class-wise summary matrix at a glance. */}
      {!loading && grouped.length > 0 && (
        <Card data-testid="rate-cards-yearwise-summary" className="border-clay/30 bg-gradient-to-br from-clay/5 via-white to-moss/5">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.12em] text-clay">
              <CalendarRange className="h-4 w-4" /> Year-wise · Class-wise Coverage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px] text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-ink-700">
                    <th className="py-2 pr-3">Period</th>
                    {CLASS_OPTIONS.map((c) => (
                      <th key={c.v} className="px-2 py-2 text-center">{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {grouped.map(([period, list]) => {
                    const byClass = Object.fromEntries(list.map((c) => [c.className, c]));
                    return (
                      <tr key={period} className="border-b border-border/60 last:border-0">
                        <td className="py-2 pr-3 font-semibold text-ink-900">{period}</td>
                        {CLASS_OPTIONS.map((c) => {
                          const card = byClass[c.v];
                          return (
                            <td key={c.v} className="px-2 py-2 text-center" data-testid={`rate-cards-cell-${period}-${c.v}`}>
                              {card ? (
                                <button
                                  type="button"
                                  onClick={() => setDraft({ ...card })}
                                  className="inline-flex items-center gap-1 rounded-full bg-moss/15 px-2.5 py-1 text-xs font-semibold text-moss-dark hover:bg-moss/25"
                                  title={`Edit · ₹${Number(card.cookingCostPerStu || 0).toFixed(2)}/student/day`}
                                >
                                  <IndianRupee className="h-3 w-3" />
                                  {Number(card.cookingCostPerStu || 0).toFixed(2)}
                                </button>
                              ) : (
                                <span className="text-xs text-ink-700/50">—</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-[0.7rem] text-ink-700">
              ₹ values are per-student / day cooking cost. Tap any badge to edit that rate card. <span className="text-ink-700/60">Empty cells = no rate card defined for that class in that period.</span>
            </p>
          </CardContent>
        </Card>
      )}

      <div className="space-y-5">
        {grouped.map(([period, list]) => (
          <Card key={period} data-testid={`rate-cards-period-${period}`}>
            <CardHeader className="pb-2">
              <CardTitle className="flex flex-wrap items-center gap-2 text-base">
                <CalendarRange className="h-4 w-4 text-clay" />
                <span className="font-bold">{period}</span>
                <span className="text-xs font-normal text-ink-700">
                  · {list.length} class row{list.length === 1 ? "" : "s"}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {[...list]
                .sort((a, b) => (CLASS_ORDER[a.className] ?? 99) - (CLASS_ORDER[b.className] ?? 99))
                .map((c) => (
                <div
                  key={c.id}
                  className="rounded-xl border border-border bg-white p-3 shadow-sm"
                  data-testid={`rate-card-row-${c.id}`}
                >
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="rounded-full bg-clay/10 px-2.5 py-1 text-xs font-semibold text-clay-dark">
                      {CLASS_LABEL[c.className] || c.className}
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs text-ink-700">
                      <IndianRupee className="h-3 w-3" />
                      <b>{Number(c.cookingCostPerStu || 0).toFixed(2)}</b> / student / day
                    </span>
                    <span className="text-xs text-ink-700">
                      {c.effectiveFrom} → {c.effectiveTo || "open"}
                    </span>
                    <div className="ml-auto flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setDraft({ ...c })} data-testid={`rate-card-edit-${c.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => onDuplicate(c)} data-testid={`rate-card-dup-${c.id}`}>
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => onDelete(c)} className="text-destructive hover:text-destructive" data-testid={`rate-card-del-${c.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  {(c.ingredients || []).length > 0 && (() => {
                    const totalCost = c.ingredients.reduce((s, i) => s + rowCost(i), 0);
                    return (
                      <div className="mt-2 overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-sand-100 text-ink-900">
                            <tr>
                              <th className="border border-border p-1.5 text-left">Ingredient</th>
                              <th className="border border-border p-1.5 text-right">Qty / student / day</th>
                              <th className="border border-border p-1.5 text-right">Rate</th>
                              <th className="border border-border p-1.5 text-right">Cost / student (Rs)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {c.ingredients.map((ing, i) => {
                              const ru = rateUnitFor(ing);
                              const cost = rowCost(ing);
                              return (
                                <tr key={i} className="border-t border-border">
                                  <td className="border border-border p-1.5">{ing.name}</td>
                                  <td className="border border-border p-1.5 text-right font-mono">{ing.qtyG} {ing.unit || "g"}</td>
                                  <td className="border border-border p-1.5 text-right font-mono">Rs {Number(ing.ratePerKg || 0).toFixed(2)} / {ru}</td>
                                  <td className="border border-border p-1.5 text-right font-mono">{cost.toFixed(2)}</td>
                                </tr>
                              );
                            })}
                            <tr className="border-t-2 border-clay/30 bg-amber-50 font-bold">
                              <td className="border border-border p-1.5" colSpan={3}>Total ingredient cost / student / day</td>
                              <td className="border border-border p-1.5 text-right font-mono">Rs {totalCost.toFixed(2)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                  {c.notes && <p className="mt-2 text-[0.7rem] text-ink-700">📝 {c.notes}</p>}
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Editor dialog */}
      <Dialog open={!!draft} onOpenChange={(o) => { if (!o) setDraft(null); }}>
        <DialogContent
          data-testid="rate-card-editor"
          className="w-[calc(100vw-1.5rem)] max-w-3xl overflow-y-auto p-0 max-h-[90vh]"
        >
          <DialogHeader className="border-b border-border bg-clay/5 px-5 py-3">
            <DialogTitle className="font-heading text-base font-bold text-ink-900">
              {draft?.id ? "Edit Rate Card" : "New Rate Card"}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Period + class combination. All school-facing calculations (Monthly MDM Bill,
              Quarterly UC, Hand Receipts, MDCF) will use the matching active card.
            </DialogDescription>
          </DialogHeader>

          {draft && (
            <div className="space-y-4 px-5 py-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div>
                  <Label htmlFor="rc-period" className="text-[11px] font-semibold uppercase tracking-[0.16em]">Period label</Label>
                  <Input
                    id="rc-period"
                    value={draft.period}
                    placeholder="e.g. FY 2025-26 or Q3 FY 2025-26"
                    onChange={(e) => setDraft({ ...draft, period: e.target.value })}
                    data-testid="rate-card-period-input"
                  />
                </div>
                <div>
                  <Label htmlFor="rc-from" className="text-[11px] font-semibold uppercase tracking-[0.16em]">Effective From</Label>
                  <Input id="rc-from" type="date" value={draft.effectiveFrom} onChange={(e) => setDraft({ ...draft, effectiveFrom: e.target.value })} data-testid="rate-card-from-input" />
                </div>
                <div>
                  <Label htmlFor="rc-to" className="text-[11px] font-semibold uppercase tracking-[0.16em]">Effective To <span className="text-ink-700 normal-case">(optional)</span></Label>
                  <Input id="rc-to" type="date" value={draft.effectiveTo || ""} onChange={(e) => setDraft({ ...draft, effectiveTo: e.target.value })} data-testid="rate-card-to-input" />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <Label className="text-[11px] font-semibold uppercase tracking-[0.16em]">Class</Label>
                  <select
                    value={draft.className}
                    onChange={(e) => setDraft({ ...draft, className: e.target.value })}
                    data-testid="rate-card-class-select"
                    className="mt-1 w-full rounded-md border border-border bg-white px-3 py-2 text-sm outline-none focus:border-clay"
                  >
                    {CLASS_OPTIONS.map((c) => (
                      <option key={c.v} value={c.v}>{c.label} · {c.od}</option>
                    ))}
                  </select>
                  {/* iter145 — teach admin how the two Daily-Entry groups
                      map to these four class cards so they don't get
                      confused when only one class reflects. */}
                  <p className="mt-1.5 text-[0.65rem] leading-snug text-ink-700/85">
                    Daily Entry groups <b>Bal Vatika + Primary</b> as one calc, and
                    <b> Upper Primary + High School</b> as another. When two cards in the same group
                    have different quantities, the <b>most recently saved card wins</b>.
                  </p>
                </div>
                <div>
                  <Label htmlFor="rc-cost" className="text-[11px] font-semibold uppercase tracking-[0.16em]">Cooking cost / student / day (Rs)</Label>
                  <Input
                    id="rc-cost"
                    type="number"
                    step="0.01"
                    value={draft.cookingCostPerStu}
                    onChange={(e) => setDraft({ ...draft, cookingCostPerStu: parseFloat(e.target.value) || 0 })}
                    onBlur={(e) => {
                      // iter142 — auto-distribute on blur. When the admin finishes
                      // editing the target cost, rescale every non-zero ingredient
                      // rate so the sum equals this new target. Saves an extra click.
                      const target = parseFloat(e.target.value) || 0;
                      if (target <= 0) return;
                      const computed = (draft.ingredients || []).reduce((s, ing) => s + rowCost(ing), 0);
                      if (computed <= 0) return; // nothing to scale from
                      if (Math.abs(target - computed) < 0.005) return; // already aligned
                      const factor = target / computed;
                      const nextIngs = draft.ingredients.map((ing) => {
                        const r = Number(ing.ratePerKg) || 0;
                        if (r === 0) return ing;
                        return { ...ing, ratePerKg: Math.round(r * factor * 100) / 100 };
                      });
                      setDraft({ ...draft, ingredients: nextIngs });
                      toast.success(`Ingredient rates auto-rescaled ×${factor.toFixed(3)} to match Rs ${target.toFixed(2)}`);
                    }}
                    data-testid="rate-card-cost-input"
                  />
                  {/* iter141 — live computed total + manual rescale button as a
                      safety net (also lets admins re-trigger after editing
                      ingredient qty/unit without re-entering the target). */}
                  <DistributeCostHint draft={draft} setDraft={setDraft} />
                </div>
              </div>

              <div>
                <div className="mb-1 flex items-center gap-2">
                  <Label className="text-[11px] font-semibold uppercase tracking-[0.16em]">Ingredients (per student per day)</Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setDraft({ ...draft, ingredients: [...draft.ingredients, { name: "", qtyG: 0, unit: "g", ratePerKg: 0 }] })}
                    data-testid="rate-card-add-ing"
                    className="ml-auto h-7 gap-1 text-xs"
                  >
                    <Plus className="h-3 w-3" /> Add row
                  </Button>
                </div>
                <div className="overflow-x-auto rounded-md border border-border">
                  <table className="w-full text-xs">
                    <thead className="bg-sand-100">
                      <tr>
                        <th className="border-b border-border p-1.5 text-left">Name</th>
                        <th className="border-b border-border p-1.5 text-right">Qty</th>
                        <th className="border-b border-border p-1.5 text-left">Unit</th>
                        <th className="border-b border-border p-1.5 text-right">Rate / unit (Rs)<br/><span className="text-[0.6rem] font-normal text-ink-700">/ kg for g · / L for ml · / pc for piece</span></th>
                        <th className="border-b border-border p-1.5 text-right">Cost / student (Rs)<br/><span className="text-[0.6rem] font-normal text-ink-700">Editable · back-computes rate</span></th>
                        <th className="border-b border-border p-1.5 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {draft.ingredients.map((ing, i) => (
                        <tr key={i} className="border-b border-border" data-testid={`rate-card-ing-row-${i}`}>
                          <td className="p-1"><input type="text" value={ing.name} onChange={(e) => setDraft((d) => { const a = [...d.ingredients]; a[i] = { ...a[i], name: e.target.value }; return { ...d, ingredients: a }; })} className="w-full rounded-md border border-border bg-white px-2 py-1 outline-none focus:border-clay" /></td>
                          <td className="p-1"><input type="number" step="0.01" value={ing.qtyG} onChange={(e) => setDraft((d) => { const a = [...d.ingredients]; a[i] = { ...a[i], qtyG: parseFloat(e.target.value) || 0 }; return { ...d, ingredients: a }; })} className="w-24 rounded-md border border-border bg-white px-2 py-1 text-right font-mono outline-none focus:border-clay" /></td>
                          <td className="p-1"><input type="text" value={ing.unit} onChange={(e) => setDraft((d) => { const a = [...d.ingredients]; a[i] = { ...a[i], unit: e.target.value }; return { ...d, ingredients: a }; })} className="w-16 rounded-md border border-border bg-white px-2 py-1 outline-none focus:border-clay" /></td>
                          <td className="p-1"><input type="number" step="0.01" value={ing.ratePerKg} onChange={(e) => setDraft((d) => { const a = [...d.ingredients]; a[i] = { ...a[i], ratePerKg: parseFloat(e.target.value) || 0 }; return { ...d, ingredients: a }; })} className="w-28 rounded-md border border-border bg-white px-2 py-1 text-right font-mono outline-none focus:border-clay" data-testid={`rate-card-ing-rate-${i}`} /></td>
                          {/* iter143 — editable Cost / student column. Typing here
                              back-computes ratePerKg for this row while keeping
                              qty untouched. Formula depends on unit:
                                • g  → rate/kg = cost × 1000 / qty
                                • ml → rate/L  = cost × 1000 / qty
                                • pc → rate/pc = cost / qty  */}
                          <td className="p-1">
                            <input
                              type="number"
                              step="0.01"
                              value={Number(rowCost(ing).toFixed(4))}
                              onChange={(e) => setDraft((d) => {
                                const a = [...d.ingredients];
                                const cur = a[i];
                                const q = Number(cur.qtyG) || 0;
                                const newCost = parseFloat(e.target.value) || 0;
                                if (q === 0) {
                                  toast.error(`Set a Qty for ${cur.name || "this row"} before editing its cost`);
                                  return d;
                                }
                                const u = (cur.unit || "g").toLowerCase();
                                const usesPerKilo = (u === "g" || u === "kg" || u === "ml" || u === "l");
                                const newRate = usesPerKilo ? (newCost * 1000) / q : newCost / q;
                                a[i] = { ...cur, ratePerKg: Math.round(newRate * 100) / 100 };
                                return { ...d, ingredients: a };
                              })}
                              className="w-28 rounded-md border border-clay/40 bg-clay/5 px-2 py-1 text-right font-mono outline-none focus:border-clay"
                              data-testid={`rate-card-ing-cost-${i}`}
                              title="Edit to auto-set the Rate/unit for this ingredient"
                            />
                          </td>
                          <td className="p-1 text-center">
                            <button
                              type="button"
                              onClick={() => setDraft((d) => ({ ...d, ingredients: d.ingredients.filter((_, j) => j !== i) }))}
                              className="text-destructive hover:text-destructive"
                              data-testid={`rate-card-ing-remove-${i}`}
                              aria-label="Remove ingredient row"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                      {draft.ingredients.length === 0 && (
                        <tr><td colSpan={6} className="p-4 text-center text-ink-700">No ingredients — click "Add row".</td></tr>
                      )}
                      {draft.ingredients.length > 0 && (
                        <tr className="bg-moss/5 font-semibold" data-testid="rate-card-ing-total-row">
                          <td className="p-1.5 text-right" colSpan={4}>Total per student / day</td>
                          <td className="p-1.5 text-right font-mono text-moss-dark">
                            Rs {draft.ingredients.reduce((s, ing) => s + rowCost(ing), 0).toFixed(2)}
                          </td>
                          <td></td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <Label htmlFor="rc-notes" className="text-[11px] font-semibold uppercase tracking-[0.16em]">Notes (optional)</Label>
                <Input id="rc-notes" value={draft.notes} placeholder="e.g. Updated as per PM-POSHAN Govt order dated 12-Jan-2026" onChange={(e) => setDraft({ ...draft, notes: e.target.value })} data-testid="rate-card-notes-input" />
              </div>
            </div>
          )}

          <DialogFooter className="flex flex-col-reverse gap-2 border-t border-border bg-sand-50 px-5 py-3 sm:flex-row sm:items-center sm:justify-end">
            <Button variant="outline" onClick={() => setDraft(null)} data-testid="rate-card-cancel"><X className="mr-1 h-4 w-4" /> Cancel</Button>
            <Button onClick={onSave} data-testid="rate-card-save" className="bg-clay text-white hover:bg-clay-dark"><Save className="mr-1 h-4 w-4" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Seed-defaults dialog — bulk-create 4 class rows from the canonical
          PM-POSHAN sheet (parity with mealPlan.js). */}
      <Dialog open={seedOpen} onOpenChange={(o) => { if (!o) setSeedOpen(false); }}>
        <DialogContent data-testid="seed-defaults-dialog" className="max-w-md p-0">
          <DialogHeader className="border-b border-border bg-moss/5 px-5 py-3">
            <DialogTitle className="font-heading text-base font-bold text-ink-900">
              Seed PM-POSHAN Default Rate Cards
            </DialogTitle>
            <DialogDescription className="text-xs">
              Bulk-creates the 4 class rate cards (Bal Vatika / Primary / Upper Primary / High School)
              using canonical PM-POSHAN per-student quantities and procurement rates from the
              app meal-plan. Edit any row afterwards.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 px-5 py-4">
            <div>
              <Label htmlFor="seed-period" className="text-[11px] font-semibold uppercase tracking-[0.16em]">Period label</Label>
              <Input id="seed-period" value={seed.period} onChange={(e) => setSeed({ ...seed, period: e.target.value })} data-testid="seed-period-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="seed-from" className="text-[11px] font-semibold uppercase tracking-[0.16em]">Effective From</Label>
                <Input id="seed-from" type="date" value={seed.effectiveFrom} onChange={(e) => setSeed({ ...seed, effectiveFrom: e.target.value })} data-testid="seed-from-input" />
              </div>
              <div>
                <Label htmlFor="seed-to" className="text-[11px] font-semibold uppercase tracking-[0.16em]">Effective To</Label>
                <Input id="seed-to" type="date" value={seed.effectiveTo || ""} onChange={(e) => setSeed({ ...seed, effectiveTo: e.target.value })} data-testid="seed-to-input" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={seed.overwrite}
                onChange={(e) => setSeed({ ...seed, overwrite: e.target.checked })}
                data-testid="seed-overwrite"
              />
              Overwrite existing rows for this period (otherwise skip)
            </label>
          </div>
          <DialogFooter className="flex flex-col-reverse gap-2 border-t border-border bg-sand-50 px-5 py-3 sm:flex-row sm:items-center sm:justify-end">
            <Button variant="outline" onClick={() => setSeedOpen(false)} data-testid="seed-cancel">
              <X className="mr-1 h-4 w-4" /> Cancel
            </Button>
            <Button onClick={onSeedDefaults} data-testid="seed-confirm" className="bg-moss text-white hover:bg-moss-dark">
              <Sparkles className="mr-1 h-4 w-4" /> Seed 4 class rows
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}


// iter141 — Live "computed total vs target" badge + one-click rescale that
// distributes the entered target across all ingredient rates proportionally
// to each ingredient's current share of the total. Ingredients with rate==0
// are left untouched (otherwise scaling them would multiply 0 forever).
function DistributeCostHint({ draft, setDraft }) {
  const target = Number(draft.cookingCostPerStu) || 0;
  const computed = (draft.ingredients || []).reduce((s, ing) => s + rowCost(ing), 0);
  const delta = target - computed;
  const aligned = Math.abs(delta) < 0.005;

  const distribute = () => {
    if (target <= 0) {
      toast.error("Enter a positive Cost / student first");
      return;
    }
    if (computed <= 0) {
      toast.error("Enter base rates for at least one ingredient — can't scale from zero");
      return;
    }
    const factor = target / computed;
    const nextIngs = draft.ingredients.map((ing) => {
      const r = Number(ing.ratePerKg) || 0;
      if (r === 0) return ing; // skip zero-rate rows
      // round to 2 decimals so the UI stays clean
      return { ...ing, ratePerKg: Math.round(r * factor * 100) / 100 };
    });
    setDraft({ ...draft, ingredients: nextIngs });
    toast.success(`Rates rescaled by ×${factor.toFixed(3)} to match Rs ${target.toFixed(2)} / student`);
  };

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[0.72rem]">
      <span
        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono font-semibold ${
          aligned ? "bg-moss/15 text-moss-dark" : "bg-clay/15 text-clay-dark"
        }`}
        data-testid="rate-card-computed-total"
      >
        Computed: Rs {computed.toFixed(2)}
        {!aligned && <span className="ml-1 text-[0.65rem] opacity-80">(Δ {delta >= 0 ? "+" : ""}{delta.toFixed(2)})</span>}
      </span>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={distribute}
        disabled={aligned || target <= 0 || computed <= 0}
        className="h-7 gap-1 px-2 text-[0.7rem]"
        data-testid="rate-card-distribute-btn"
        title="Scale each ingredient's rate proportionally so the sum equals the cost above"
      >
        <RefreshCw className="h-3 w-3" /> Rescale now
      </Button>
      <span className="text-[0.65rem] text-ink-700/70">
        Tip: rates auto-rescale when you tab out of the Cost field. Zero-rate rows are skipped.
      </span>
    </div>
  );
}
